// Admin access requests (arc 101 slice 01) — the operator's inbox of waitlist
// applications, and the ONE click that turns an application into an invited,
// approved-on-accept, seated user.
//
// The row is `AccessRequestView` from `@developerz/domain`, imported and never
// mirrored: it is the projection `apps/api` serializes, so a field that moves
// there fails to compile here rather than rendering `undefined`.
//
// THE INVITE IS A PROVISION. `POST /v1/admin/access-requests/:id/invite` runs
// the same control as `POST /v1/admin/provision` and the MCP
// `admin access_requests_invite` action (`apps/api/src/deps/provision.ts`,
// #3703 pattern): invite → approve →
// grant → one audit row. The body is `AccessRequestInviteSchema`, and every
// knob defaults to `DEFAULT_INVITE_BUNDLE` server-side — the form still SENDS the
// numbers it shows, so what the operator saw is what the audit row records,
// and a departure from the bundle is a value they typed. The box budget is NOT
// a knob: it is derived from the seats (`enrollmentBoxBudget`,
// `packages/billing/src/tiers.ts`), and `boxes` is not a user-subject grant.
//
// THE ACCEPT LINK IS SHOWN ONCE. The provision answer carries the invitation's
// `acceptUrl`; no listing ever will (`InvitationView` has no token by design),
// so the caller renders it with a copy affordance and the mail as best-effort:
// the API mints, then mails, and a mail failure does not fail the call
// (`apps/api/src/deps/invitations.ts` § "The mail is BEST-EFFORT").
//
// Reads follow `lib/adminOss.ts`: cursor-paged, the status filter ALWAYS sent
// in the shared `?filter=status:<value>` grammar, `all` a real value.

import {
  type AccessRequestStatus,
  type AccessRequestView,
  DEFAULT_INVITE_BUNDLE,
} from '@developerz/domain';
import { createQuery } from '@tanstack/solid-query';
import { apiFetch } from './api';
import { type OperatorOutcome, withOperatorCredential } from './operator-credential';
import type { Page } from './paged';
import { SESSION_TOKEN } from './session';

export type { AccessRequestStatus, AccessRequestView };
export { DEFAULT_INVITE_BUNDLE };

/** Inbox filter — `all` drops the status fence; anything else pins one status. */
export type AdminAccessFilter = AccessRequestStatus | 'all';

/** The filter vocabulary, in queue order: decide first, then look back. */
export const ADMIN_ACCESS_FILTERS: readonly AdminAccessFilter[] = [
  'pending',
  'invited',
  'declined',
  'all',
];

/**
 * What the invite form sends — the input half of `AccessRequestInviteSchema`
 * (`z.input`, where every field is optional and defaults to the bundle).
 * Sent EXPLICITLY here so the audit row records the numbers the operator saw.
 */
export type InviteAccessRequestInput = {
  readonly ai_devs: number;
  /** How long the grant lasts; `null` = ongoing (plan 102 slice 05). */
  readonly expires_in_days: number | null;
  readonly reason?: string;
};

/**
 * The invite twin's answer (`apps/api/src/routes/admin.ts`, seat C): the
 * decided row, the bundle as APPLIED — `boxes` is the box budget the server
 * derived from the seats, the one place this screen can read it — the
 * provision's three effects, and the audit row tying them together.
 * `invitation` is null when the applicant had already signed in (nothing to
 * mail; they are approved and granted outright).
 *
 * Refusals: 404 (no such request), 409 (already decided; `detail` names the
 * invitation), 422 (the control's own refusal) — each a `detail` the dialog
 * renders verbatim.
 */
export type AccessRequestProvisioned = {
  readonly request: AccessRequestView;
  readonly bundle: {
    readonly ai_devs: number;
    readonly expires_in_days: number | null;
    readonly boxes: number;
  };
  readonly user_id: string | null;
  readonly login: string | null;
  readonly email: string | null;
  readonly invitation: {
    readonly invitationId: string;
    readonly acceptUrl: string;
    readonly expiresAtIso: string;
    readonly reissued: boolean;
    /** Whether the invitation mail LEFT. "On its way" is said only when true (deep sweep 03-13). */
    readonly mailed: boolean;
  } | null;
  readonly approval: 'approved' | 'already_approved' | 'not_yet_registered';
  readonly grants: readonly {
    readonly grant_id: string;
    readonly kind: string;
    readonly amount: number | null;
    readonly expires_at_iso: string | null;
  }[];
  readonly audit_id: string | null;
};

export const ADMIN_ACCESS_REQUESTS_KEY = 'admin_access_requests';

const BASE = '/v1/admin/access-requests';

/** One page of the inbox, newest-first. Idle (no fetch) with no session. */
export function useAdminAccessRequests(
  status: () => AdminAccessFilter,
  cursor: () => string | null,
  perPage = 30,
  token: string | null = SESSION_TOKEN,
) {
  return createQuery<Page<AccessRequestView>>(() => ({
    queryKey: [ADMIN_ACCESS_REQUESTS_KEY, token, status(), cursor(), perPage],
    enabled: token !== null,
    queryFn: () =>
      apiFetch<Page<AccessRequestView>>(
        `${BASE}?${queueParams(status(), cursor(), perPage)}`,
        token,
      ),
  }));
}

/**
 * Invite + provision one application, on an operator credential carrying BOTH
 * `admin:grants` (the route's scope) and `admin:users` (its guard: the provision
 * approves the person too). 2xx with the provision result; every refusal is a
 * 422 whose `detail` the caller renders verbatim.
 */
export function inviteAccessRequest(
  id: string,
  body: InviteAccessRequestInput,
  token: string | null = SESSION_TOKEN,
): Promise<OperatorOutcome<AccessRequestProvisioned>> {
  return withOperatorCredential(
    {
      scopes: ['admin:grants', 'admin:users'],
      name: 'admin console: invite an access applicant',
      token,
    },
    (send) =>
      send<AccessRequestProvisioned>(`${BASE}/${encodeURIComponent(id)}/invite`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  );
}

/**
 * Decline one. The route takes an optional `{ reason? }`; this door sends none
 * — a decline reason has no reader on this screen yet, and a field with no
 * reader is a knob. The caller re-reads the inbox; the echoed row is not
 * patched in.
 */
export function declineAccessRequest(
  id: string,
  token: string | null = SESSION_TOKEN,
): Promise<OperatorOutcome<void>> {
  return withOperatorCredential(
    { scopes: ['admin:users'], name: 'admin console: decline an access applicant', token },
    (send) => send<void>(`${BASE}/${encodeURIComponent(id)}/decline`, { method: 'POST' }),
  );
}

/**
 * The shared list grammar (`lib/admin.ts` `listParams`), with `status` ALWAYS
 * sent — including `all` — for the reason `lib/adminOss.ts` states: this is a
 * decision queue that defaults to `pending`, so an omitted term would hide the
 * decided rows the filter just asked for.
 */
function queueParams(status: AdminAccessFilter, cursor: string | null, perPage: number): string {
  const params = new URLSearchParams({ filter: `status:${status}`, per_page: String(perPage) });
  if (cursor) params.set('cursor', cursor);
  return params.toString();
}
